import React from "react";

// Tailwind CSS required. Save as app/fair-use/page.tsx or pages/fair-use.tsx
// Bilingual EN/AR, same layout pattern as your Privacy & Terms pages.

export default function FairUsePolicyPage() {
  const [lang, setLang] = React.useState<"en" | "ar">("en");

  const Section: React.FC<{ id: string; title: string; children: React.ReactNode }> = ({ id, title, children }) => (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-2xl font-semibold mb-3">{title}</h2>
      <div className="prose max-w-none dark:prose-invert">{children}</div>
    </section>
  );

  const NavLink: React.FC<{ href: string; label: string }> = ({ href, label }) => (
    <a href={href} className="text-sm px-3 py-1 rounded-full border hover:bg-gray-50 dark:hover:bg-gray-800 transition">
      {label}
    </a>
  );

  return (
    <main className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-50">
      <div className="mx-auto max-w-4xl px-4 py-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Fair Use Policy</h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Last updated: October 24, 2025</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm">Language:</span>
            <div className="inline-flex rounded-xl border overflow-hidden">
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 text-sm ${lang === "en" ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-transparent"}`}
              >EN</button>
              <button
                onClick={() => setLang("ar")}
                className={`px-3 py-1.5 text-sm ${lang === "ar" ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-transparent"}`}
              >AR</button>
            </div>
          </div>
        </div>

        {/* Grid with sticky TOC */}
        <div className="grid grid-cols-1 md:grid-cols-[220px,1fr] gap-6">
          <aside className="md:sticky md:top-6 h-max border rounded-2xl p-3 md:p-4 bg-neutral-50 dark:bg-neutral-900/40">
            <nav className="flex flex-wrap md:flex-col gap-2">
              <NavLink href="#intro" label={lang === "en" ? "Introduction" : "مقدمة"} />
              <NavLink href="#scope" label={lang === "en" ? "Scope" : "النطاق"} />
              <NavLink href="#guidelines" label={lang === "en" ? "Fair Usage" : "الاستخدام العادل"} />
              <NavLink href="#monitoring" label={lang === "en" ? "Monitoring & Enforcement" : "المراقبة والتطبيق"} />
              <NavLink href="#responsibilities" label={lang === "en" ? "Your Responsibilities" : "مسؤولياتك"} />
              <NavLink href="#changes" label={lang === "en" ? "Changes" : "التغييرات"} />
              <NavLink href="#contact" label={lang === "en" ? "Contact" : "التواصل"} />
            </nav>
          </aside>

          <div className="flex flex-col gap-8">
            {lang === "en" ? (
              <div>
                <Section id="intro" title="1. Introduction">
                  <p>
                    We aim to provide all customers with a high-quality, reliable loyalty-card platform. To keep the Service stable and responsive for everyone, this Fair Use Policy ("Policy") explains how to use the Service responsibly and without abuse.
                  </p>
                </Section>

                <Section id="scope" title="2. Scope">
                  <p>
                    This Policy applies to all merchant accounts using the Loyalty Stamper / Roaait platform operated by <strong>ROAA DATA IT W.L.L</strong>. It covers behaviours that may jeopardise performance, degrade user experience, or create disproportionate load or risk.
                  </p>
                </Section>

                <Section id="guidelines" title="3. Fair Usage Guidelines">
                  <h3 className="text-lg font-semibold mt-0">Acceptable usage</h3>
                  <ul>
                    <li>Issuing digital loyalty cards to genuine customers in the ordinary course of your business.</li>
                    <li>Sending reasonable in-app push notifications to your existing customers who opted in.</li>
                    <li>Stamping/redeeming at a rate consistent with your store traffic and plan limits.</li>
                  </ul>
                  <h3 className="text-lg font-semibold mt-6">Excessive or abusive usage (examples)</h3>
                  <ul>
                    <li>Bulk or automated issuance generating thousands of cards or stamps per minute without genuine customer engagement.</li>
                    <li>Mass messaging or spam to non-customers or purchased lists.</li>
                    <li>Any use that materially degrades platform performance for others or circumvents technical limits.</li>
                  </ul>
                  <p className="mt-3">We may define or adjust reasonable technical thresholds (e.g., rate limits) to protect platform stability.</p>
                </Section>

                <Section id="monitoring" title="4. Monitoring and Enforcement">
                  <ul>
                    <li>We monitor aggregate usage patterns to detect abnormal or abusive behaviour.</li>
                    <li>We may contact you for clarification and request adjustments to your usage.</li>
                    <li>We may apply temporary rate limits, feature restrictions, or suspensions where needed.</li>
                    <li>Repeated or egregious breaches may result in account termination under the Terms & Conditions.</li>
                  </ul>
                </Section>

                <Section id="responsibilities" title="5. Your Responsibilities">
                  <ul>
                    <li>Use the Service honestly and only for your business’s real customers.</li>
                    <li>Do not spam, spoof, abuse, or attempt to bypass technical limits.</li>
                    <li>Respond promptly to our communications regarding compliance with this Policy.</li>
                  </ul>
                </Section>

                <Section id="changes" title="6. Changes to this Policy">
                  <p>
                    We may update this Policy from time to time. Significant changes will be communicated via email or in-app notice and will take effect upon posting with an updated date.
                  </p>
                </Section>

                <Section id="contact" title="7. Contact">
                  <p>
                    Questions about this Policy? Contact <a href="mailto:info@roaait.com">info@roaait.com</a>.
                  </p>
                </Section>
              </div>
            ) : (
              <div dir="rtl" className="text-right">
                <Section id="intro" title="1. مقدمة">
                  <p>
                    نهدف إلى توفير منصة موثوقة وعالية الجودة لجميع العملاء. للحفاظ على استقرار الخدمة وسرعة استجابتها للجميع، توضح سياسة الاستخدام العادل هذه كيفية استخدام الخدمة بشكل مسؤول ودون إساءة.
                  </p>
                </Section>

                <Section id="scope" title="2. النطاق">
                  <p>
                    تنطبق هذه السياسة على جميع حسابات التجار على منصة Loyalty Stamper / Roaait التي تديرها <strong>شركة رؤى لتقنية المعلومات ذ.م.م</strong>. وتشمل السلوكيات التي قد تؤثر سلبًا على الأداء أو تجربة المستخدمين أو تشكل حملًا أو مخاطر غير متناسبة.
                  </p>
                </Section>

                <Section id="guidelines" title="3. إرشادات الاستخدام العادل">
                  <h3 className="text-lg font-semibold mt-0">استخدام مقبول</h3>
                  <ul>
                    <li>إصدار بطاقات ولاء رقمية لعملائك الحقيقيين ضمن نشاطك التجاري المعتاد.</li>
                    <li>إرسال إشعارات معقولة داخل التطبيق لعملائك الحاليين الذين وافقوا على ذلك.</li>
                    <li>عمليات ختم/استرداد متوافقة مع عدد زوار متجرك وحدود خطتك.</li>
                  </ul>
                  <h3 className="text-lg font-semibold mt-6">أمثلة على الاستخدام المفرط أو المسيء</h3>
                  <ul>
                    <li>إصدار آلي أو جماعي ينتج آلاف البطاقات أو الأختام في الدقيقة دون تفاعل عملاء حقيقي.</li>
                    <li>إرسال رسائل جماعية أو بريد عشوائي لأشخاص ليسوا عملاءك أو لقوائم مشتراة.</li>
                    <li>أي استخدام يؤدي إلى تدهور ملحوظ في أداء المنصة للآخرين أو يتجاوز القيود التقنية.</li>
                  </ul>
                  <p className="mt-3">يجوز لنا تحديد أو ضبط حدود تقنية معقولة (مثل حدود المعدل) لحماية استقرار المنصة.</p>
                </Section>

                <Section id="monitoring" title="4. المراقبة والتطبيق">
                  <ul>
                    <li>نراقب أنماط الاستخدام المجمعة لاكتشاف السلوك غير الطبيعي أو المسيء.</li>
                    <li>قد نتواصل معك لطلب توضيحات وطلب تعديلات على الاستخدام.</li>
                    <li>قد نفرض حدودًا مؤقتة للمعدل أو قيودًا على الميزات أو تعليقًا عند الحاجة.</li>
                    <li>قد يؤدي التكرار أو الانتهاكات الجسيمة إلى إنهاء الحساب وفقًا للشروط والأحكام.</li>
                  </ul>
                </Section>

                <Section id="responsibilities" title="5. مسؤولياتك">
                  <ul>
                    <li>استخدم الخدمة بأمانة ولأجل عملاء نشاطك الحقيقيين فقط.</li>
                    <li>لا ترسل رسائل عشوائية أو تحاول التحايل على القيود التقنية.</li>
                    <li>استجب بسرعة لرسائلنا المتعلقة بالامتثال لهذه السياسة.</li>
                  </ul>
                </Section>

                <Section id="changes" title="6. التغييرات على هذه السياسة">
                  <p>
                    قد نقوم بتحديث هذه السياسة من حين لآخر. سيتم إخطارك بالتغييرات الجوهرية عبر البريد الإلكتروني أو داخل التطبيق وستصبح نافذة عند النشر مع تحديث التاريخ.
                  </p>
                </Section>

                <Section id="contact" title="7. التواصل">
                  <p>
                    للاستفسارات حول هذه السياسة، تواصل معنا عبر <a href="mailto:info@roaait.com">info@roaait.com</a>.
                  </p>
                </Section>
              </div>
            )}
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-10 text-xs text-neutral-500 dark:text-neutral-400">
          <p>
            This page is for transparency and does not constitute legal advice. For legal interpretation, please seek qualified counsel.
          </p>
        </div>
      </div>
    </main>
  );
}
